import React, { useState, useEffect } from 'react';
import { FaMoneyBillAlt, FaFileAlt } from "react-icons/fa";
import { CgProfile } from "react-icons/cg";
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Button from 'react-bootstrap/Button';
import './payroll.css';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
 
export default function Payroll() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showTable, setShowTable] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [payrollData, setPayrollData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userName, setUserName] = useState('');
  const navigate = useNavigate();
  const username = localStorage.getItem('name'); // Retrieve the username from storage
 
  // Pagination state
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
 
  const validateForm = () => {
    const errors = {};
    if (!startDate) errors.startDate = 'Start Date is required';
    if (!endDate) errors.endDate = 'End Date is required';
   
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      errors.dateRange = 'End date must be after start date';
    }
   
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
 
  // Fetch payroll data from API
  const fetchPayrollData = async () => {
    try {
      setLoading(true);
      setError(null);
     
      // Get the logged-in user's ID
      const userId = localStorage.getItem('userId');
     
      if (!userId) {
        throw new Error('User ID not found. Please log in again.');
      }
     
      const response = await axios.get('https://backend.ems.hiradentallab.com/api/payroll/user', {
        params: { userId }
      });
     
      if (response.data.data && response.data.data.length > 0) {
        setPayrollData(response.data.data);
        // Set the user name from the first record
        setUserName(response.data.data[0].name || '');
      } else {
        setPayrollData([]);
        setUserName('');
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching payroll data:", err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch payroll data');
      setLoading(false);
    }
  };
 
  // Filter data based on date range
  const filterDataByDateRange = () => {
    if (!startDate || !endDate) return payrollData;
   
    const start = new Date(startDate);
    const end = new Date(endDate);
   
    return payrollData.filter(item => {
      const itemStartDate = new Date(item.startDate);
      const itemEndDate = new Date(item.endDate);
      return (itemStartDate >= start && itemStartDate <= end) ||
             (itemEndDate >= start && itemEndDate <= end);
    });
  };
 
  const handleGenerateReport = () => {
    if (validateForm()) {
      // If we haven't fetched data yet, fetch it
      if (payrollData.length === 0) {
        fetchPayrollData();
      } else {
        // Filter the existing data
        const filtered = filterDataByDateRange();
        setFilteredData(filtered);
        setShowTable(true);
      }
    }
  };
 
  // Update filtered data when payrollData or dates change
  useEffect(() => {
    if (payrollData.length > 0 && startDate && endDate) {
      const filtered = filterDataByDateRange();
      setFilteredData(filtered);
      setShowTable(true);
    }
  }, [payrollData, startDate, endDate]);
 
  // Pagination logic
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when data changes
  }, [filteredData]);
 
  const currentTableData = () => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };
 
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= Math.ceil(filteredData.length / rowsPerPage)) {
      setCurrentPage(pageNumber);
    }
  };
 
  const pageRange = () => {
    const pages = [];
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);
 
    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }
 
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };
 
  const exportToExcel = () => {
    const headers = [
      "Employee ID",
      "Name",
      "Basic Salary",
      "Working Days",
      "Days Worked",
      "Late Minutes Deduction",
      "Early Close Deduction",
      "Extra Minutes Bonus",
      "Public Holiday Pay",
      "Final Salary",
      "Period Start",
      "Period End"
    ];
   
    const excelData = filteredData.map(item => ([
      item.userId || 'N/A',
      item.name || 'N/A',
      item.basicSalary || '0',
      item.totalWorkingDays || '0',
      item.totalDaysWorked || '0',
      item.lateMinutesDeduction || '0',
      item.earlyCloseDeduction || '0',
      item.extraMinutesBonus || '0',
      item.publicHolidayPay || '0',
      item.finalSalary || '0',
      item.startDate ? new Date(item.startDate).toLocaleDateString() : 'N/A',
      item.endDate ? new Date(item.endDate).toLocaleDateString() : 'N/A'
    ]));
   
    const ws = XLSX.utils.aoa_to_sheet([headers, ...excelData]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payroll Report");
    XLSX.writeFile(wb, `Payroll_Report_${startDate}_to_${endDate}.xlsx`);
  };
 
  const exportToPDF = () => {
    const doc = new jsPDF();
   
    // Add title
    doc.setFontSize(18);
    doc.text(`Payroll Report (${startDate} to ${endDate})`, 10, 15);
   
    // Add table headers
    doc.setFontSize(10);
    const headers = [
      { title: "Emp ID", width: 15 },
      { title: "Name", width: 30 },
      { title: "Basic", width: 15 },
      { title: "Work Days", width: 15 },
      { title: "Days Worked", width: 15 },
      { title: "Late Deduct", width: 15 },
      { title: "Early Deduct", width: 15 },
      { title: "Extra Bonus", width: 15 },
      { title: "Holiday Pay", width: 15 },
      { title: "Final Salary", width: 15 }
    ];
   
    let xPosition = 10;
    headers.forEach(header => {
      doc.text(header.title, xPosition, 30);
      xPosition += header.width;
    });
   
    // Add data rows
    let yPosition = 40;
    filteredData.forEach(item => {
      xPosition = 10;
     
      // Employee ID
      doc.text(item.userId?.toString() || 'N/A', xPosition, yPosition);
      xPosition += headers[0].width;
     
      // Name
      doc.text(item.name || 'N/A', xPosition, yPosition);
      xPosition += headers[1].width;
     
      // Basic Salary
      doc.text(item.basicSalary?.toString() || '0', xPosition, yPosition);
      xPosition += headers[2].width;
     
      // Working Days
      doc.text(item.totalWorkingDays?.toString() || '0', xPosition, yPosition);
      xPosition += headers[3].width;
     
      // Days Worked
      doc.text(item.totalDaysWorked?.toString() || '0', xPosition, yPosition);
      xPosition += headers[4].width;
     
      // Late Deduction
      doc.text(item.lateMinutesDeduction?.toString() || '0', xPosition, yPosition);
      xPosition += headers[5].width;
     
      // Early Deduction
      doc.text(item.earlyCloseDeduction?.toString() || '0', xPosition, yPosition);
      xPosition += headers[6].width;
     
      // Extra Bonus
      doc.text(item.extraMinutesBonus?.toString() || '0', xPosition, yPosition);
      xPosition += headers[7].width;
     
      // Holiday Pay
      doc.text(item.publicHolidayPay?.toString() || '0', xPosition, yPosition);
      xPosition += headers[8].width;
     
      // Final Salary
      doc.text(item.finalSalary?.toString() || '0', xPosition, yPosition);
     
      yPosition += 10;
     
      if (yPosition > 280) {
        doc.addPage();
        yPosition = 30;
      }
    });
    doc.save(`Payroll_Report_${startDate}_to_${endDate}.pdf`);
  };
  const handlePayinClick = (payrollId) => {
    navigate(`/pay-invoice/${payrollId}`);
  };
 
 
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 2
    }).format(amount);
  };
 
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
 
  return (
    <div >
      <div style={{padding:'10px'}}>
        <h1 className='payroll-title'>
          <FaMoneyBillAlt className='payroll-icon' /> Payroll Reports
        </h1>
        <hr />
        <h1 className='payroll-subtitle'>
          <CgProfile className='payroll-icon' />{username ? ` ${username}!` : 'guest'}
 
        </h1>
        <hr />
 
        <Form className='payroll-form'>
          <Row>
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label>Start date <span className='required'>*</span> :</Form.Label>
                <Form.Control
                  type="date"
                  required
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  isInvalid={!!formErrors.startDate}
                />
                <Form.Control.Feedback type="invalid">{formErrors.startDate}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label>End date <span className='required'>*</span> :</Form.Label>
                <Form.Control
                  type="date"
                  required
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  isInvalid={!!formErrors.endDate}
                />
                <Form.Control.Feedback type="invalid">{formErrors.endDate}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          {formErrors.dateRange && (
            <div className="text-danger mb-3">{formErrors.dateRange}</div>
          )}
          <Button
            className='generate-report-button'
            variant='primary'
            type='button'
            onClick={handleGenerateReport}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </Form>
 
        {error && (
          <div className="alert alert-danger mt-3">
            {error}
          </div>
        )}
 
        {showTable && (
          <div className='payroll-table-container'>
            <div className='table-actions'>
              <Row className='w-100'>
                <Col xs={12} md={6} className='d-flex align-items-center justify-content-start'>
                  <Button variant="secondary" className='export-button' onClick={exportToExcel} disabled={filteredData.length === 0}>
                    Export to Excel
                  </Button>
                  <Button variant="secondary" className='export-button ms-2' onClick={exportToPDF} disabled={filteredData.length === 0}>
                    Export to PDF
                  </Button>
                </Col>
                <Col xs={12} md={6} className='d-flex align-items-center justify-content-end'>
                  <Form className="d-flex justify-content-end">
                    <Form.Control
                      type="search"
                      placeholder="Search"
                      aria-label="Search"
                      className="search-bar"
                      // Implement search functionality if needed
                    />
                  </Form>
                </Col>
              </Row>
            </div>
           
            {loading ? (
              <div className="text-center py-4">Loading payroll data...</div>
            ) : filteredData.length === 0 ? (
              <div className="text-center py-4">No payroll records found for the selected date range</div>
            ) : (
              <div className="table-responsive">
                <table className='table table-striped payroll-table'>
                  <thead className='payroll-table-header-footer'>
                    <tr className="center-text">
                      <th>Name</th>
                      <th>Period Start</th>
                      <th>Period End</th>
                      <th>Basic Salary</th>
                      <th>Working Days</th>
                      <th>Days Worked</th>
                      <th>Absent Deduction</th>
                      <th>OverTime</th>
                      <th>Holiday Pay</th>
                      <th>Final Salary</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="center-text">
                    {currentTableData().map(item => (
                      <tr key={item._id}>
                        <td>{item.name || 'N/A'}</td>
                        <td>{formatDate(item.startDate)}</td>
                        <td>{formatDate(item.endDate)}</td>
                        <td>{formatCurrency(item.basicSalary)}</td>
                        <td>{item.totalWorkingDays}</td>
                        <td>{item.totalDaysWorked}</td>
                        <td>{formatCurrency(item.totalMinutesDeduction)}</td>
                        <td>{formatCurrency(item.extraMinutesBonus)}</td>
                        <td>{formatCurrency(item.publicHolidayPay)}</td>
                        <td>{formatCurrency(item.finalSalary)}</td>
                        <td>
                          <div className="d-flex justify-content-center">
                            <Button
                              variant="primary"
                              size='sm'
                              className="me-2"
                              onClick={() => handlePayinClick(item._id)}
                            >
                              <FaFileAlt />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
               
                </table>
 
                <div className="pagination-container">
                  <Row className="custom-row">
                    <Col xs={12} md={9} className="custom-col-left">
                      Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, filteredData.length)} of {filteredData.length} entries
                    </Col>
                    <Col xs={12} md={3} className="custom-col-right">
                      <nav>
                        <ul className="pagination">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
                          </li>
                          {pageRange().map((pageNumber) => (
                            <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => handlePageChange(pageNumber)}>{pageNumber}</button>
                            </li>
                          ))}
                          <li className={`page-item ${currentPage === Math.ceil(filteredData.length / rowsPerPage) ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                          </li>
                        </ul>
                      </nav>
                    </Col>
                  </Row>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 